import * as XLSX from 'xlsx';
import { Timestamp } from 'firebase/firestore';
import { fuelTransactionsService } from './fuel-transactions';
import { shipmentsService } from './shipments';
import { vehicleMappingService } from './vehicle-mapping';
import { odometerService } from './odometer';
import { odometerAutomationService } from './odometer-automation';
import { vehiclesService } from './vehicles';
import { licensePlatesMatch, normalizeLicensePlate, generatePlateVariations } from '../../utils/arabic-english-converter';
import { enhancedFuelImportService } from './fuel-import-enhanced';

class FuelImportService {
  constructor() {
    this.supportedFormats = ['.xlsx', '.xls'];
    this.maxFileSize = 10 * 1024 * 1024; // 10MB
    this.batchSize = 500;
  }

  // Validate file before processing
  validateFile(file) {
    const errors = [];

    // Check file size
    if (file.size > this.maxFileSize) {
      errors.push('File size exceeds 10MB limit');
    }

    // Check file format
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!this.supportedFormats.includes(fileExtension)) {
      errors.push('Unsupported file format. Please use .xlsx or .xls files');
    }

    return errors;
  }

  // Parse Excel file and extract fuel transaction data
  async parseExcelFile(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });

      // Get the first sheet
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // Convert to JSON with header row handling
      const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (data.length < 2) {
        throw new Error('File appears to be empty or has no data rows');
      }

      // Find the header row (should be row 1, but let's be flexible)
      let headerRowIndex = -1;
      for (let i = 0; i < Math.min(data.length, 5); i++) {
        const row = data[i];
        if (Array.isArray(row) && row.includes('Invoice number')) {
          headerRowIndex = i;
          break;
        }
      }

      if (headerRowIndex === -1) {
        throw new Error('Could not find header row with expected columns');
      }

      const headers = data[headerRowIndex];
      const dataRows = data.slice(headerRowIndex + 1);

      // Filter out empty rows and summary rows
      const validDataRows = dataRows.filter(row => {
        return Array.isArray(row) &&
          row.length > 0 &&
          row[0] &&
          typeof row[0] === 'number' && // Invoice number should be numeric
          row[2]; // Vehicle plate should exist
      });

      // Map to our expected structure
      const transactions = validDataRows.map((row, index) => {
        try {
          return this.mapRowToTransaction(row, headers, index);
        } catch (error) {
          console.error(`Error mapping row ${index + headerRowIndex + 2}:`, error);
          throw new Error(`Error in row ${index + headerRowIndex + 2}: ${error.message}`);
        }
      });

      return {
        transactions,
        totalRows: validDataRows.length,
        fileName: file.name
      };
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      throw new Error(`Failed to parse Excel file: ${error.message}`);
    }
  }

  // Map Excel row to fuel transaction object
  mapRowToTransaction(row, headers, rowIndex) {
    try {
      // Create mapping based on header positions
      const getColumnValue = (columnName) => {
        const index = headers.indexOf(columnName);
        return index !== -1 ? row[index] : null;
      };

      // Parse date string to Timestamp
      const parseFillTime = (dateString) => {
        if (!dateString) return null;

        try {
          // Handle different date formats
          let date;
          if (typeof dateString === 'string') {
            // Format: "2025-08-05 13:44:37"
            date = new Date(dateString);
          } else if (typeof dateString === 'number') {
            // Excel date serial number
            date = XLSX.SSF.parse_date_code(dateString);
            date = new Date(date.y, date.m - 1, date.d, date.H, date.M, date.S);
          } else {
            date = new Date(dateString);
          }

          if (isNaN(date.getTime())) {
            throw new Error('Invalid date format');
          }

          return Timestamp.fromDate(date);
        } catch (error) {
          console.error('Error parsing date:', dateString, error);
          return null;
        }
      };

      // Clean and normalize vehicle plate (handle Arabic-English conversion)
      const cleanVehiclePlate = (plate) => {
        if (!plate) return '';
        return normalizeLicensePlate(plate);
      };

      // Parse station coordinates if available
      const parseCoordinates = () => {
        const lat = getColumnValue('Latitude') || getColumnValue('Station Latitude');
        const lng = getColumnValue('Longitude') || getColumnValue('Station Longitude');

        if (lat && lng) {
          const latitude = parseFloat(lat);
          const longitude = parseFloat(lng);

          if (!isNaN(latitude) && !isNaN(longitude)) {
            return { lat: latitude, lng: longitude };
          }
        }
        return null;
      };

      const transaction = {
        invoice_number: getColumnValue('Invoice number')?.toString() || null,
        vehicle_plate: cleanVehiclePlate(getColumnValue('Vehicle')),
        fill_time: parseFillTime(getColumnValue('Date')),
        liters: parseFloat(getColumnValue('Number of liters')) || 0,
        cost: parseFloat(getColumnValue('Cost')) || 0,
        odometer: parseFloat(getColumnValue('Odometer')) || null,
        driver: getColumnValue('delegate')?.toString() || null,
        station_name: getColumnValue('The station')?.toString() || null,
        station_coordinates: parseCoordinates(),
        matched: false,
        shipment_id: null
      };

      // Validate required fields
      if (!transaction.vehicle_plate) {
        throw new Error('Vehicle plate is required');
      }
      if (!transaction.fill_time) {
        throw new Error('Fill time is required');
      }
      if (transaction.liters <= 0) {
        throw new Error('Liters must be greater than 0');
      }
      if (transaction.cost <= 0) {
        throw new Error('Cost must be greater than 0');
      }

      return transaction;
    } catch (error) {
      throw new Error(`Row validation failed: ${error.message}`);
    }
  }

  // Automatically match fuel transactions to shipments
  async autoMatchTransactions(companyId, transactions) {
    try {
      // Get all shipments for the company
      const shipments = await shipmentsService.getShipments(companyId);

      console.log('🚛 Total shipments loaded:', shipments.length);
      console.log('⛽ Total transactions to match:', transactions.length);

      // Filter shipments that have vehicle plates
      const shipmentsWithVehicles = shipments.filter(s => s.vehiclePlate);
      console.log('🚛 Shipments with vehicle plates:', shipmentsWithVehicles.length);

      // Get unique vehicle plates for debugging
      const uniquePlates = [...new Set(shipmentsWithVehicles.map(s => s.vehiclePlate))];
      console.log('🔍 Unique vehicle plates in system:', uniquePlates.slice(0, 10));

      // Log sample transaction data for debugging
      if (transactions.length > 0) {
        const sampleTransaction = transactions[0];
        console.log('⛽ Sample transaction data:', {
          invoice_number: sampleTransaction.invoice_number,
          vehicle_plate: sampleTransaction.vehicle_plate,
          normalized_plate: normalizeLicensePlate(sampleTransaction.vehicle_plate),
          fill_time: sampleTransaction.fill_time,
          liters: sampleTransaction.liters,
          cost: sampleTransaction.cost
        });
      }

      // Log sample shipment data for debugging
      if (shipmentsWithVehicles.length > 0) {
        const sampleShipment = shipmentsWithVehicles[0];
        console.log('📋 Sample shipment data:', {
          id: sampleShipment.id,
          trackingNumber: sampleShipment.trackingNumber,
          vehiclePlate: sampleShipment.vehiclePlate,
          normalized_plate: normalizeLicensePlate(sampleShipment.vehiclePlate),
          deliveryDate: sampleShipment.deliveryDate,
          pickupDate: sampleShipment.pickupDate,
          createdAt: sampleShipment.createdAt,
          status: sampleShipment.status
        });
      }

      const matchedTransactions = [];
      const unmatchedTransactions = [];

      for (const transaction of transactions) {
        console.log('\n🔄 Processing transaction:', transaction.invoice_number);
        const matchedShipment = this.findMatchingShipment(transaction, shipmentsWithVehicles);

        if (matchedShipment) {
          console.log('✅ Transaction matched to shipment:', matchedShipment.trackingNumber || matchedShipment.id);
          matchedTransactions.push({
            ...transaction,
            shipment_id: matchedShipment.id,
            matched: true,
            matchedShipment: matchedShipment // Include full shipment details
          });
        } else {
          console.log('❌ Transaction remains unmatched');
          unmatchedTransactions.push(transaction);
        }
      }

      const matchRate = transactions.length > 0 ? matchedTransactions.length / transactions.length : 0;
      console.log(`📊 Final matching results: ${matchedTransactions.length}/${transactions.length} (${(matchRate * 100).toFixed(1)}%)`);

      return {
        matched: matchedTransactions,
        unmatched: unmatchedTransactions,
        matchRate
      };
    } catch (error) {
      console.error('Error auto-matching transactions:', error);
      throw error;
    }
  }

  // Find matching shipment for a fuel transaction
  findMatchingShipment(transaction, shipments) {
    // Use mapped vehicle plate if available, otherwise use original
    const transactionPlate = transaction.mappedVehiclePlate || transaction.vehicle_plate;
    console.log('🔍 Looking for shipment match for vehicle plate:', transactionPlate);
    console.log('🔍 Normalized transaction plate:', normalizeLicensePlate(transactionPlate));
    console.log('🔍 Transaction plate variations:', generatePlateVariations(transactionPlate));

    // Filter shipments by vehicle plate using Arabic-English aware matching
    const vehicleMatches = shipments.filter(shipment => {
      if (!shipment.vehiclePlate) return false;

      const match = licensePlatesMatch(transactionPlate, shipment.vehiclePlate);
      if (match) {
        console.log('✅ Found vehicle match:', transactionPlate, 'matches', shipment.vehiclePlate);
        console.log('   - Transaction normalized:', normalizeLicensePlate(transactionPlate));
        console.log('   - Shipment normalized:', normalizeLicensePlate(shipment.vehiclePlate));
      }
      return match;
    });

    console.log('📊 Found', vehicleMatches.length, 'vehicle matches for', transactionPlate);

    // Add debugging info to transaction
    transaction.hasVehicleMatch = vehicleMatches.length > 0;
    transaction.potentialMatches = vehicleMatches.length;

    if (vehicleMatches.length === 0) {
      // Enhanced debugging for no matches
      const availablePlates = shipments
        .filter(s => s.vehiclePlate)
        .slice(0, 10)
        .map(s => ({
          original: s.vehiclePlate,
          normalized: normalizeLicensePlate(s.vehiclePlate),
          trackingNumber: s.trackingNumber || s.id
        }));

      console.log('❌ No matches found. Available shipment plates (first 10):', availablePlates);
      console.log('❌ Total shipments with vehicle plates:', shipments.filter(s => s.vehiclePlate).length);

      // Set debugging info
      transaction.unmatchedReason = 'مركبة غير موجودة في النظام';
      transaction.hasVehicleMatch = false;
      transaction.hasTimeMatch = false;

      // Try to find similar plates for debugging
      const similarPlates = availablePlates.filter(plate => {
        const similarity = this.calculatePlateSimilarity(transactionPlate, plate.original);
        return similarity > 0.5;
      });

      if (similarPlates.length > 0) {
        console.log('🔍 Similar plates found (>50% match):', similarPlates);
        transaction.unmatchedReason = `مركبة مشابهة موجودة (${similarPlates.length} مطابقات محتملة)`;

        // If we have a very high similarity match (>90%), use it as a fallback
        const highSimilarityMatch = similarPlates.find(plate => {
          const similarity = this.calculatePlateSimilarity(transactionPlate, plate.original);
          return similarity > 0.9;
        });

        if (highSimilarityMatch) {
          console.log('🎯 Using high similarity fallback match:', highSimilarityMatch);
          const fallbackShipment = shipments.find(s => s.vehiclePlate === highSimilarityMatch.original);
          if (fallbackShipment) {
            console.log('✅ Fallback match found, continuing with time-based matching...');
            transaction.hasVehicleMatch = true;
            return this.findMatchingShipmentByTime([fallbackShipment], transaction);
          }
        }
      }

      return null;
    }

    const transactionTime = transaction.fill_time.toDate();
    const transactionDate = this.getDateOnly(transactionTime);
    console.log('⏰ Transaction time:', transactionTime.toISOString());
    console.log('📅 Transaction date:', transactionDate.toDateString());

    // Log vehicle matches with their times
    console.log('🚛 Vehicle matches with times:');
    vehicleMatches.forEach((shipment, index) => {
      const shipmentTime = this.getShipmentTime(shipment);
      console.log(`  ${index + 1}. ${shipment.trackingNumber || shipment.id} - ${shipment.vehiclePlate} - ${shipmentTime ? shipmentTime.toISOString() : 'NO TIME'}`);
    });

    // SPECIAL BUSINESS RULE: Same-day matching priority
    // Step 1: Find shipments on the same calendar day as the fuel fill
    const sameDayShipments = vehicleMatches.filter(shipment => {
      const shipmentTime = this.getShipmentTime(shipment);
      if (!shipmentTime) {
        console.log('⚠️ Shipment has no time:', shipment.trackingNumber || shipment.id);
        return false;
      }

      const shipmentDate = this.getDateOnly(shipmentTime);
      const isSame = this.isSameDay(transactionDate, shipmentDate);
      console.log(`📅 Comparing dates: ${transactionDate.toDateString()} vs ${shipmentDate.toDateString()} = ${isSame}`);
      return isSame;
    });

    console.log('🎯 Same-day shipments found:', sameDayShipments.length);

    // If there are shipments on the same day, assign to the earliest one
    if (sameDayShipments.length > 0) {
      const earliest = sameDayShipments.reduce((earliest, shipment) => {
        const shipmentTime = this.getShipmentTime(shipment);
        const earliestTime = this.getShipmentTime(earliest);

        return shipmentTime < earliestTime ? shipment : earliest;
      });
      console.log('✅ Matched to same-day shipment:', earliest.trackingNumber || earliest.id);
      return earliest;
    }

    // Step 2: If no same-day shipments, find the most recent shipment before the fill time
    const shipmentsBeforeFill = vehicleMatches.filter(shipment => {
      const shipmentTime = this.getShipmentTime(shipment);
      const isBefore = shipmentTime && shipmentTime < transactionTime;
      console.log(`⏰ Shipment ${shipment.trackingNumber || shipment.id} time check: ${shipmentTime ? shipmentTime.toISOString() : 'NO TIME'} < ${transactionTime.toISOString()} = ${isBefore}`);
      return isBefore;
    });

    console.log('📈 Shipments before fill time:', shipmentsBeforeFill.length);

    if (shipmentsBeforeFill.length > 0) {
      // Return the most recent shipment before the fill time
      const mostRecent = shipmentsBeforeFill.reduce((mostRecent, shipment) => {
        const shipmentTime = this.getShipmentTime(shipment);
        const mostRecentTime = this.getShipmentTime(mostRecent);

        return shipmentTime > mostRecentTime ? shipment : mostRecent;
      });
      console.log('✅ Matched to most recent before fill:', mostRecent.trackingNumber || mostRecent.id);
      return mostRecent;
    }

    // Step 3: If no shipments before fill time, assign to earliest shipment
    if (vehicleMatches.length > 0) {
      const earliestShipment = vehicleMatches.reduce((earliest, shipment) => {
        const shipmentTime = this.getShipmentTime(shipment);
        const earliestTime = this.getShipmentTime(earliest);
        return shipmentTime < earliestTime ? shipment : earliest;
      });
      console.log('✅ Matched to earliest shipment (transaction before all shipments):', earliestShipment.trackingNumber || earliestShipment.id);
      return earliestShipment;
    }

    console.log('❌ No suitable shipment found for time-based matching');
    return null;
  }

  // Helper method to get shipment time consistently
  getShipmentTime(shipment) {
    // Try multiple date fields in order of preference
    const dateFields = ['deliveryDate', 'pickupDate', 'createdAt', 'updatedAt'];

    for (const field of dateFields) {
      if (shipment[field]) {
        try {
          let date;
          const value = shipment[field];

          if (value.seconds) {
            // Firestore Timestamp
            date = new Date(value.seconds * 1000);
          } else if (value.toDate) {
            // Firestore Timestamp with toDate method
            date = value.toDate();
          } else {
            // Regular date string or Date object
            date = new Date(value);
          }

          if (!isNaN(date.getTime())) {
            console.log(`📅 Using ${field} for shipment ${shipment.trackingNumber || shipment.id}:`, date.toISOString());
            return date;
          }
        } catch (error) {
          console.warn(`⚠️ Error parsing ${field} for shipment ${shipment.trackingNumber || shipment.id}:`, error);
        }
      }
    }

    console.warn(`⚠️ No valid date found for shipment ${shipment.trackingNumber || shipment.id}`);
    return null;
  }

  // Helper method to get date only (without time)
  getDateOnly(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  // Helper method to check if two dates are the same day
  isSameDay(date1, date2) {
    return date1.getTime() === date2.getTime();
  }

  // Helper method to calculate plate similarity (for debugging)
  calculatePlateSimilarity(plate1, plate2) {
    if (!plate1 || !plate2) return 0;

    const norm1 = normalizeLicensePlate(plate1);
    const norm2 = normalizeLicensePlate(plate2);

    if (norm1 === norm2) return 1.0;

    // Calculate simple character overlap
    const maxLength = Math.max(norm1.length, norm2.length);
    if (maxLength === 0) return 0;

    let commonChars = 0;
    const minLength = Math.min(norm1.length, norm2.length);

    for (let i = 0; i < minLength; i++) {
      if (norm1[i] === norm2[i]) {
        commonChars++;
      }
    }

    return commonChars / maxLength;
  }

  // Check if vehicle mapping is required for the import
  async checkVehicleMappingRequirements(companyId, transactions) {
    try {
      // Get existing vehicle mappings
      const existingMappings = await vehicleMappingService.getVehicleMappings(companyId);

      // Get all vehicles and shipments for matching
      const [vehicles, shipments] = await Promise.all([
        vehiclesService.getVehicles(companyId),
        shipmentsService.getShipments(companyId)
      ]);

      // Get unique vehicle plates from transactions
      const uniqueTransactionPlates = [...new Set(transactions.map(t => t.vehicle_plate))];

      console.log('🔍 Checking vehicle mapping requirements...');
      console.log('📋 Transaction plates:', uniqueTransactionPlates);
      console.log('📋 Existing mappings:', existingMappings);

      const unmappedPlates = [];
      const suggestedMappings = [];

      for (const transactionPlate of uniqueTransactionPlates) {
        // Check if mapping already exists
        if (existingMappings[transactionPlate]) {
          console.log(`✅ Mapping exists for ${transactionPlate} -> ${existingMappings[transactionPlate]}`);
          continue;
        }

        // Find potential matches in system
        const potentialMatches = vehicleMappingService.findPotentialMatches(
          transactionPlate,
          vehicles
        );

        // Also check shipment vehicle plates
        const shipmentPlates = [...new Set(shipments
          .filter(s => s.vehiclePlate && s.vehiclePlate !== 'Unknown Vehicle')
          .map(s => s.vehiclePlate)
        )];

        const shipmentMatches = vehicleMappingService.findPotentialMatches(
          transactionPlate,
          shipmentPlates.map(plate => ({ plateNumber: plate }))
        );

        // Combine and sort all matches
        const allMatches = [...potentialMatches, ...shipmentMatches]
          .sort((a, b) => b.similarity - a.similarity)
          .slice(0, 5); // Top 5 matches

        unmappedPlates.push(transactionPlate);
        suggestedMappings.push({
          petroAppPlate: transactionPlate,
          suggestions: allMatches,
          transactionCount: transactions.filter(t => t.vehicle_plate === transactionPlate).length
        });

        console.log(`❓ Need mapping for ${transactionPlate}, found ${allMatches.length} suggestions`);
      }

      return {
        requiresMapping: unmappedPlates.length > 0,
        unmappedPlates,
        suggestedMappings,
        existingMappings
      };

    } catch (error) {
      console.error('Error checking vehicle mapping requirements:', error);
      throw error;
    }
  }

  // Process import with confirmed vehicle mappings
  async processImportWithMappings(companyId, importData, confirmedMappings, userId) {
    try {
      console.log('🔄 Processing import with confirmed mappings...');

      // Save the new mappings
      for (const mapping of confirmedMappings) {
        if (mapping.systemVehiclePlate && mapping.systemVehiclePlate !== 'skip') {
          await vehicleMappingService.saveVehicleMapping(
            companyId,
            mapping.petroAppPlate,
            mapping.systemVehiclePlate,
            userId
          );
          console.log(`✅ Saved mapping: ${mapping.petroAppPlate} -> ${mapping.systemVehiclePlate}`);
        }
      }

      // Apply mappings to transactions
      const updatedTransactions = this.applyVehicleMappingsToTransactions(
        importData.transactions,
        confirmedMappings
      );

      // Now proceed with auto-matching - Use enhanced matching
      const matchResult = await enhancedFuelImportService.autoMatchTransactions(companyId, updatedTransactions);

      return {
        ...importData,
        matched: matchResult.matched,
        unmatched: matchResult.unmatched,
        matchRate: matchResult.matchRate,
        readyToImport: true,
        requiresVehicleMapping: false
      };

    } catch (error) {
      console.error('Error processing import with mappings:', error);
      throw error;
    }
  }

  // Apply vehicle mappings to transactions
  applyVehicleMappingsToTransactions(transactions, mappings) {
    const mappingLookup = {};
    mappings.forEach(mapping => {
      if (mapping.systemVehiclePlate && mapping.systemVehiclePlate !== 'skip') {
        mappingLookup[mapping.petroAppPlate] = mapping.systemVehiclePlate;
      }
    });

    return transactions.map(transaction => ({
      ...transaction,
      mappedVehiclePlate: mappingLookup[transaction.vehicle_plate] || transaction.vehicle_plate
    }));
  }

  // Helper method to find matching shipment by time (extracted for reuse)
  findMatchingShipmentByTime(vehicleMatches, transaction) {
    const transactionTime = transaction.fill_time.toDate();
    const transactionDate = this.getDateOnly(transactionTime);
    console.log('⏰ Transaction time:', transactionTime.toISOString());
    console.log('📅 Transaction date:', transactionDate.toDateString());

    // Log vehicle matches with their times
    console.log('🚛 Vehicle matches with times:');
    vehicleMatches.forEach((shipment, index) => {
      const shipmentTime = this.getShipmentTime(shipment);
      console.log(`  ${index + 1}. ${shipment.trackingNumber || shipment.id} - ${shipment.vehiclePlate} - ${shipmentTime ? shipmentTime.toISOString() : 'NO TIME'}`);
    });

    // SPECIAL BUSINESS RULE: Same-day matching priority
    // Step 1: Find shipments on the same calendar day as the fuel fill
    const sameDayShipments = vehicleMatches.filter(shipment => {
      const shipmentTime = this.getShipmentTime(shipment);
      if (!shipmentTime) {
        console.log('⚠️ Shipment has no time:', shipment.trackingNumber || shipment.id);
        return false;
      }

      const shipmentDate = this.getDateOnly(shipmentTime);
      const isSame = this.isSameDay(transactionDate, shipmentDate);
      console.log(`📅 Comparing dates: ${transactionDate.toDateString()} vs ${shipmentDate.toDateString()} = ${isSame}`);
      return isSame;
    });

    console.log('🎯 Same-day shipments found:', sameDayShipments.length);

    // If there are shipments on the same day, assign to the earliest one
    if (sameDayShipments.length > 0) {
      const earliest = sameDayShipments.reduce((earliest, shipment) => {
        const shipmentTime = this.getShipmentTime(shipment);
        const earliestTime = this.getShipmentTime(earliest);

        return shipmentTime < earliestTime ? shipment : earliest;
      });
      console.log('✅ Matched to same-day shipment:', earliest.trackingNumber || earliest.id);
      return earliest;
    }

    // Step 2: If no same-day shipments, find the most recent shipment before the fill time
    const shipmentsBeforeFill = vehicleMatches.filter(shipment => {
      const shipmentTime = this.getShipmentTime(shipment);
      const isBefore = shipmentTime && shipmentTime < transactionTime;
      console.log(`⏰ Shipment ${shipment.trackingNumber || shipment.id} time check: ${shipmentTime ? shipmentTime.toISOString() : 'NO TIME'} < ${transactionTime.toISOString()} = ${isBefore}`);
      return isBefore;
    });

    console.log('📈 Shipments before fill time:', shipmentsBeforeFill.length);

    if (shipmentsBeforeFill.length > 0) {
      // Return the most recent shipment before the fill time
      const mostRecent = shipmentsBeforeFill.reduce((mostRecent, shipment) => {
        const shipmentTime = this.getShipmentTime(shipment);
        const mostRecentTime = this.getShipmentTime(mostRecent);

        return shipmentTime > mostRecentTime ? shipment : mostRecent;
      });
      console.log('✅ Matched to most recent before fill:', mostRecent.trackingNumber || mostRecent.id);
      return mostRecent;
    }

    // Step 3: If no shipments before fill time, assign to earliest shipment
    if (vehicleMatches.length > 0) {
      const earliestShipment = vehicleMatches.reduce((earliest, shipment) => {
        const shipmentTime = this.getShipmentTime(shipment);
        const earliestTime = this.getShipmentTime(earliest);
        return shipmentTime < earliestTime ? shipment : earliest;
      });
      console.log('✅ Matched to earliest shipment (transaction before all shipments):', earliestShipment.trackingNumber || earliestShipment.id);
      return earliestShipment;
    }

    console.log('❌ No suitable shipment found for time-based matching');
    return null;
  }

  // Process complete import workflow
  async processImport(companyId, file, userId) {
    try {
      // Generate import batch ID
      const importBatchId = `FUEL_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Step 1: Validate file
      const fileErrors = this.validateFile(file);
      if (fileErrors.length > 0) {
        throw new Error(fileErrors.join(', '));
      }

      // Step 2: Parse Excel file
      const parseResult = await this.parseExcelFile(file);

      // Step 3: Check for duplicates
      const duplicates = await fuelTransactionsService.checkForDuplicates(
        companyId,
        parseResult.transactions
      );

      // Filter out duplicates
      const newTransactions = parseResult.transactions.filter(
        transaction => !duplicates.some(dup =>
          (dup.invoice_number && dup.invoice_number === transaction.invoice_number) ||
          (dup.vehicle_plate === transaction.vehicle_plate &&
            dup.fill_time?.seconds === transaction.fill_time?.seconds)
        )
      );

      // Step 4: Check for vehicle mapping requirements
      const mappingResult = await this.checkVehicleMappingRequirements(companyId, newTransactions);

      if (mappingResult.requiresMapping) {
        return {
          importBatchId,
          fileName: parseResult.fileName,
          totalRows: parseResult.totalRows,
          newTransactions: newTransactions.length,
          duplicatesSkipped: duplicates.length,
          requiresVehicleMapping: true,
          vehicleMappingData: mappingResult,
          transactions: newTransactions,
          readyToImport: false
        };
      }

      // Step 4.5: Apply existing mappings to transactions
      const existingMappings = mappingResult.existingMappings || {};
      const mappingsArray = Object.entries(existingMappings).map(([petroAppPlate, systemVehiclePlate]) => ({
        petroAppPlate,
        systemVehiclePlate
      }));

      let transactionsToMatch = newTransactions;
      if (mappingsArray.length > 0) {
        transactionsToMatch = this.applyVehicleMappingsToTransactions(newTransactions, mappingsArray);
        console.log('🔗 Applied existing mappings to transactions:', mappingsArray.length);

        // Log the mapping results
        transactionsToMatch.forEach(transaction => {
          if (transaction.mappedVehiclePlate && transaction.mappedVehiclePlate !== transaction.vehicle_plate) {
            console.log(`🔗 Mapped: ${transaction.vehicle_plate} -> ${transaction.mappedVehiclePlate}`);
          }
        });
      }

      // Step 5: Auto-match transactions (after mappings are applied) - Use enhanced matching
      const matchResult = await enhancedFuelImportService.autoMatchTransactions(companyId, transactionsToMatch);

      return {
        importBatchId,
        fileName: parseResult.fileName,
        totalRows: parseResult.totalRows,
        newTransactions: newTransactions.length,
        duplicatesSkipped: duplicates.length,
        matched: matchResult.matched,
        unmatched: matchResult.unmatched,
        matchRate: matchResult.matchRate,
        readyToImport: true
      };
    } catch (error) {
      console.error('Error processing import:', error);
      throw error;
    }
  }

  // Finalize import - save transactions to database
  async finalizeImport(companyId, importData, userId) {
    try {
      const allTransactions = [...importData.matched, ...importData.unmatched];

      if (allTransactions.length === 0) {
        throw new Error('No transactions to import');
      }

      // Import in batches
      const results = [];
      for (let i = 0; i < allTransactions.length; i += this.batchSize) {
        const batch = allTransactions.slice(i, i + this.batchSize);
        const batchResults = await fuelTransactionsService.batchImportFuelTransactions(
          companyId,
          batch,
          importData.importBatchId,
          userId
        );
        results.push(...batchResults);
      }

      // Create odometer readings from fuel transactions using automation service
      let odometerSyncResult = { created: 0, errors: [] };
      try {
        console.log('🔄 Auto-syncing odometer readings from imported fuel data...');
        odometerSyncResult = await odometerAutomationService.autoSyncOnFuelImport(
          companyId,
          results,
          userId
        );
        console.log(`✅ Odometer sync completed: ${odometerSyncResult.created} readings created`);
      } catch (error) {
        console.error('❌ Error syncing odometer readings:', error);
        odometerSyncResult.errors.push(`Odometer sync failed: ${error.message}`);
      }

      // AUTO-APPLY matched transactions to shipment costings (ENHANCED LOGGING)
      let autoApplicationResult = { processedTransactions: 0, updatedCostings: 0, createdCostings: 0, errors: [] };
      try {
        console.log('🔄 Auto-applying matched fuel transactions to shipment costings...');
        console.log(`📊 Import results: ${results.length} transactions imported`);
        
        const { fuelAutoApplicationService } = await import('./fuel-auto-application');
        
        // Add a small delay to ensure transactions are fully saved
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        autoApplicationResult = await fuelAutoApplicationService.autoApplyMatchedTransactions(
          companyId,
          userId
        );
        
        console.log(`✅ Auto-application completed: ${autoApplicationResult.processedTransactions} transactions applied to ${autoApplicationResult.updatedCostings + autoApplicationResult.createdCostings} costings`);
        
        if (autoApplicationResult.errors.length > 0) {
          console.log('⚠️ Auto-application errors:');
          autoApplicationResult.errors.forEach(error => {
            console.log(`   - ${error.shipmentId || 'Unknown'}: ${error.error}`);
          });
        }
        
        if (autoApplicationResult.processedTransactions === 0) {
          console.log('⚠️ No transactions were auto-applied. This might indicate an issue.');
          console.log('💡 You may need to run the manual fix script to apply the transactions.');
        }
        
      } catch (error) {
        console.error('❌ Error auto-applying fuel transactions:', error);
        console.error('❌ Stack trace:', error.stack);
        autoApplicationResult.errors.push(`Auto-application failed: ${error.message}`);
      }

      // Log import activity
      await this.logImportActivity(companyId, {
        importBatchId: importData.importBatchId,
        fileName: importData.fileName,
        totalRows: importData.totalRows,
        importedCount: results.length,
        duplicateCount: importData.duplicatesSkipped,
        unmatchedCount: importData.unmatched.length,
        errorCount: 0,
        status: 'completed',
        createdBy: userId
      });

      return {
        success: true,
        importedCount: results.length,
        transactions: results,
        odometerReadingsCreated: odometerSyncResult.created,
        odometerSyncErrors: odometerSyncResult.errors,
        // NEW: Auto-application results
        autoApplicationResults: autoApplicationResult,
        appliedTransactions: autoApplicationResult.processedTransactions,
        updatedCostings: autoApplicationResult.updatedCostings,
        createdCostings: autoApplicationResult.createdCostings,
        autoApplicationErrors: autoApplicationResult.errors
      };
    } catch (error) {
      console.error('Error finalizing import:', error);

      // Log failed import
      await this.logImportActivity(companyId, {
        importBatchId: importData.importBatchId,
        fileName: importData.fileName,
        totalRows: importData.totalRows,
        importedCount: 0,
        duplicateCount: importData.duplicatesSkipped,
        unmatchedCount: importData.unmatched.length,
        errorCount: 1,
        status: 'failed',
        createdBy: userId,
        errorMessage: error.message
      });

      throw error;
    }
  }

  // Log import activity
  async logImportActivity(companyId, logData) {
    try {
      // This would typically go to a fuel_import_logs collection
      // For now, we'll just console.log it
      console.log('Import activity logged:', {
        companyId,
        ...logData,
        importDate: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error logging import activity:', error);
      // Don't throw - logging failure shouldn't break the import
    }
  }

  // Create odometer readings from imported fuel transactions
  async createOdometerReadings(companyId, fuelTransactions, userId) {
    try {
      const odometerReadings = [];

      // Get all vehicles for the company to match plates
      const vehicles = await vehiclesService.getVehicles(companyId);
      const vehicleMap = new Map();

      // Create mapping of plate numbers to vehicle IDs
      vehicles.forEach(vehicle => {
        const cleanPlate = vehicle.plateNumber?.toString().replace(/\s+/g, '').toUpperCase();
        if (cleanPlate) {
          vehicleMap.set(cleanPlate, vehicle.id);
        }
      });

      // Process each fuel transaction with odometer data
      for (const transaction of fuelTransactions) {
        if (transaction.odometer && transaction.odometer > 0) {
          // Find matching vehicle using Arabic-English aware matching
          const transactionPlate = transaction.mappedVehiclePlate || transaction.vehicle_plate;
          let vehicleId = null;

          // Try to find exact match first
          for (const [plate, id] of vehicleMap.entries()) {
            if (licensePlatesMatch(transactionPlate, plate)) {
              vehicleId = id;
              break;
            }
          }

          if (vehicleId) {
            try {
              // Check if odometer reading already exists for this transaction
              const existingReadings = await odometerService.getVehicleReadings(companyId, vehicleId, 10);
              const hasExistingReading = existingReadings.some(reading =>
                reading.sourceId === transaction.id
              );

              if (!hasExistingReading) {
                const readingId = await odometerService.addReading(companyId, vehicleId, {
                  reading: transaction.odometer,
                  source: 'fuel_import',
                  sourceId: transaction.id,
                  notes: `Fuel transaction at ${transaction.station_name || 'Unknown station'}`,
                  recordedBy: userId,
                  recordedAt: transaction.fill_time?.toDate?.() || new Date(transaction.fill_time)
                });

                odometerReadings.push(readingId);
              }
            } catch (error) {
              console.error(`Error creating odometer reading for vehicle ${vehicleId}:`, error);
              // Continue processing other readings even if one fails
            }
          }
        }
      }

      console.log(`Created ${odometerReadings.length} odometer readings from fuel import`);
      return odometerReadings;
    } catch (error) {
      console.error('Error creating odometer readings:', error);
      // Don't throw error to avoid breaking the import process
      return [];
    }
  }
}

export const fuelImportService = new FuelImportService();